<template>
  <div class="notulensi-form-page">
    <div class="page-header">
      <h1 class="th-title">
        {{ isEdit ? 'Edit Notulensi Kegiatan' : 'Tambah Notulensi Kegiatan' }}
      </h1>
    </div>

    <a-card class="form-card">
      <h2>Informasi Notulensi</h2>
      <a-spin :spinning="loading">
        <a-form
          ref="formRef"
          :rules="rulesForm"
          :model="formState"
          :layout="'vertical'"
          @submit.prevent="openConfirmModal"
        >
          <a-form-item label="Nama Kegiatan" ref="namaKegiatan" name="namaKegiatan">
            <a-input v-model:value="formState.namaKegiatan" placeholder="Masukkan nama kegiatan" />
          </a-form-item>

          <a-form-item label="Waktu Kegiatan" ref="waktuKegiatan" name="waktuKegiatan">
            <a-date-picker
              v-model:value="formState.waktuKegiatan"
              class="w-100"
              format="DD-MM-YYYY HH:mm"
              :show-time="{ format: 'HH:mm' }"
              placeholder="Pilih tanggal dan jam kegiatan"
            />
          </a-form-item>

          <a-form-item label="Jenis Kegiatan" ref="jenisKegiatan" name="jenisKegiatan">
            <a-radio-group v-model:value="formState.jenisKegiatan">
              <a-radio value="Internal">Internal</a-radio>
              <a-radio value="Eksternal">Eksternal</a-radio>
            </a-radio-group>
          </a-form-item>

          <a-form-item label="File Notulensi & Lainnya">
            <a-upload-dragger
              v-model:fileList="fileList"
              name="files"
              :multiple="true"
              :before-upload="() => false"
              :show-upload-list="false"
              accept=".doc,.docx,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/png,image/jpeg"
              @change="handleFileChange"
            >
              <p class="ant-upload-drag-icon upload-icon">
                <InboxOutlined />
              </p>
              <p class="ant-upload-text">Klik atau seret file ke sini untuk mengunggah</p>
              <p class="ant-upload-hint">
                Format DOC, DOCX, XLS, XLSX, PDF, PNG, JPG, atau JPEG
              </p>
            </a-upload-dragger>

            <div v-if="fileList.length" class="file-list">
              <div
                v-for="file in fileList"
                :key="file.uid"
                class="d-flex align-items-center mb-2 file-link"
              >
                <PaperClipOutlined class="mr-2 file-paperclip-icon" />
                <div class="flex-grow-1">
                  <span
                    v-if="editingFileUid !== file.uid"
                    class="mr-4 custom-text-anchor-not-clickable"
                  >
                    {{ file.displayName || file.name }}
                  </span>
                  <a-input
                    v-else
                    v-model:value="editedFileName"
                    size="small"
                    class="mr-4 file-name-input"
                    @pressEnter="() => saveFileName(file)"
                    @blur="() => saveFileName(file)"
                  />
                  <EyeOutlined class="custom-icon-unggah mr-2" @click="() => openFile(file)" />
                  <ExportOutlined class="custom-icon-unggah mr-2" @click="() => openFile(file)" />
                </div>
                <a-tooltip title="Ubah nama file">
                  <EditOutlined
                    class="mr-2 file-action-icon"
                    @click="() => startEditFileName(file)"
                  />
                </a-tooltip>
                <a-tooltip title="Hapus file">
                  <DeleteOutlined
                    class="custom-icon-unggah file-action-icon"
                    @click="() => removeFile(file)"
                  />
                </a-tooltip>
              </div>
            </div>
          </a-form-item>

          <a-form-item label="Link">
            <div class="link-rows">
              <div v-for="(link, index) in formState.links" :key="link.key" class="link-row">
                <a-input v-model:value="link.url" placeholder="Link" />
                <a-input v-model:value="link.title" placeholder="Judul link" />
                <a-tooltip title="Hapus link">
                  <button type="button" class="icon-button danger" @click="removeLink(index)">
                    <DeleteOutlined />
                  </button>
                </a-tooltip>
              </div>
            </div>
            <a-button class="btn-add-link" @click="addLink">
              <template #icon>
                <PlusOutlined />
              </template>
              Tambah Link
            </a-button>
          </a-form-item>

          <div class="form-actions">
            <button type="button" class="button-secondary" @click="goBack">Kembali</button>
            <button type="submit" class="button-primary" :disabled="submitting">
              {{ submitting ? 'Menyimpan...' : 'Simpan' }}
            </button>
          </div>
        </a-form>
      </a-spin>
    </a-card>

    <a-modal
      v-model:visible="confirmModal"
      centered
      :footer="null"
      :title="isEdit ? 'Ubah Data Notulensi?' : 'Yakin isi formulir sudah sesuai?'"
      @cancel="confirmModal = false"
    >
      <p class="confirm-copy">
        Pastikan nama kegiatan, waktu kegiatan, file, dan link yang dimasukkan sudah benar sebelum
        disimpan.
      </p>

      <div class="modal-actions">
        <button type="button" class="modal-button secondary" @click="confirmModal = false">
          KEMBALI
        </button>
        <button
          type="button"
          class="modal-button primary"
          :disabled="submitting"
          @click="handleSubmit"
        >
          {{ submitting ? 'MENYIMPAN...' : 'YA, SIMPAN' }}
        </button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'
import 'moment/locale/id'
import { notification } from 'ant-design-vue'
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  InboxOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from '@ant-design/icons-vue'
import { getFileUrl, notulensiAPI } from '@/services/axios/perki'

moment.locale('id')

const createLinkRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  url: '',
  title: '',
})

const getStoredRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('perki_user') || '{}')
    return localStorage.getItem('perki_role') || user.role || ''
  } catch (error) {
    return localStorage.getItem('perki_role') || ''
  }
}

const getFileExtension = name => {
  const match = name ? name.match(/\.[^/.]+$/) : null
  return match ? match[0] : ''
}

export default defineComponent({
  name: 'NotulensiForm',
  components: {
    DeleteOutlined,
    EditOutlined,
    ExportOutlined,
    EyeOutlined,
    InboxOutlined,
    PaperClipOutlined,
    PlusOutlined,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref()
    const loading = ref(false)
    const submitting = ref(false)
    const confirmModal = ref(false)
    const fileList = ref([])
    const deletedFileIds = ref([])
    const editingFileUid = ref('')
    const editedFileName = ref('')

    const isEdit = computed(() => Boolean(route.params.id))
    const isSekretaris = computed(() => getStoredRole() === 'sekretaris')

    const formState = reactive({
      namaKegiatan: '',
      waktuKegiatan: null,
      jenisKegiatan: 'Internal',
      links: [createLinkRow()],
    })

    const rulesForm = {
      namaKegiatan: [
        {
          required: true,
          message: 'Harap masukkan nama kegiatan!',
          trigger: 'change',
        },
      ],
      waktuKegiatan: [
        {
          required: true,
          message: 'Harap pilih waktu kegiatan!',
          trigger: 'change',
          type: 'object',
        },
      ],
      jenisKegiatan: [
        {
          required: true,
          message: 'Harap pilih jenis kegiatan!',
          trigger: 'change',
        },
      ],
    }

    const normalizeFile = file => ({
      ...file,
      displayName: file.displayName || file.name,
    })

    const fetchDetail = async () => {
      loading.value = true
      try {
        const response = await notulensiAPI.getById(route.params.id)
        const data = response.data?.data

        if (!data) {
          notification.error({
            message: 'Data Tidak Ditemukan',
            description: 'Notulensi yang dipilih tidak tersedia.',
          })
          router.replace('/notulensi')
          return
        }

        formState.namaKegiatan = data.namaKegiatan || ''
        formState.waktuKegiatan = data.waktuKegiatan ? moment(data.waktuKegiatan) : null
        formState.jenisKegiatan = data.jenisKegiatan || 'Internal'
        formState.links =
          data.links && data.links.length
            ? data.links.map(link => ({
                key: link.id || `${Date.now()}-${Math.random()}`,
                id: link.id,
                url: link.url || '',
                title: link.title || '',
              }))
            : [createLinkRow()]

        fileList.value =
          data.files && data.files.length
            ? data.files.map(file => ({
                uid: file.id,
                id: file.id,
                name: file.fileName,
                displayName: file.fileName,
                fileUrl: file.fileUrl,
                status: 'done',
              }))
            : []
      } catch (error) {
        notification.error({
          message: 'Gagal Memuat Notulensi',
          description: error.response?.data?.message || 'Silakan coba lagi beberapa saat lagi.',
        })
        router.replace('/notulensi')
      } finally {
        loading.value = false
      }
    }

    const handleFileChange = info => {
      fileList.value = (info.fileList || []).map(normalizeFile)
    }

    const startEditFileName = file => {
      editingFileUid.value = file.uid
      editedFileName.value = file.displayName || file.name
    }

    const saveFileName = file => {
      if (editingFileUid.value !== file.uid) return

      const nextName = editedFileName.value.trim()
      if (!nextName) {
        editingFileUid.value = ''
        editedFileName.value = ''
        return
      }

      const currentExtension = getFileExtension(file.displayName || file.name)
      const nextExtension = getFileExtension(nextName)
      const displayName =
        currentExtension && !nextExtension ? `${nextName}${currentExtension}` : nextName

      file.displayName = displayName
      if (file.originFileObj) {
        file.name = displayName
      }

      editingFileUid.value = ''
      editedFileName.value = ''
    }

    const removeFile = file => {
      if (file.id) {
        deletedFileIds.value.push(file.id)
      }
      fileList.value = fileList.value.filter(item => item.uid !== file.uid)
    }

    const openFile = file => {
      if (file.fileUrl) {
        window.open(getFileUrl(file.fileUrl), '_blank')
        return
      }

      if (file.originFileObj) {
        const objectUrl = URL.createObjectURL(file.originFileObj)
        window.open(objectUrl, '_blank')
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      }
    }

    const addLink = () => {
      formState.links.push(createLinkRow())
    }

    const removeLink = index => {
      if (formState.links.length === 1) {
        formState.links[0].url = ''
        formState.links[0].title = ''
        return
      }

      formState.links.splice(index, 1)
    }

    const validateLinks = () => {
      const incompleteLink = formState.links.find(link => {
        const url = (link.url || '').trim()
        const title = (link.title || '').trim()
        return (url && !title) || (!url && title)
      })

      if (incompleteLink) {
        notification.warning({
          message: 'Link Belum Lengkap',
          description: 'Setiap link perlu diisi bersama judul link.',
        })
        return false
      }

      return true
    }

    const openConfirmModal = async event => {
      event?.preventDefault?.()

      if (!validateLinks()) return

      try {
        await formRef.value.validate()
        confirmModal.value = true
      } catch (error) {
        if (error?.outOfDate && Array.isArray(error?.errorFields) && !error.errorFields.length) {
          await nextTick()
          await formRef.value.validate()
          confirmModal.value = true
        }
      }
    }

    const buildFormData = () => {
      const formData = new FormData()

      formData.append('namaKegiatan', formState.namaKegiatan.trim())
      formData.append('waktuKegiatan', formState.waktuKegiatan.format('YYYY-MM-DD HH:mm:ss'))
      formData.append('jenisKegiatan', formState.jenisKegiatan)

      const links = formState.links
        .map(link => ({
          title: (link.title || '').trim(),
          url: (link.url || '').trim(),
        }))
        .filter(link => link.title || link.url)
      formData.append('links', JSON.stringify(links))

      fileList.value.forEach(file => {
        if (file.originFileObj) {
          const filename = file.displayName || file.name
          formData.append('files', file.originFileObj, filename)
        }
      })

      if (isEdit.value) {
        formData.append('deletedFileIds', JSON.stringify(deletedFileIds.value))
        formData.append(
          'updatedFiles',
          JSON.stringify(
            fileList.value
              .filter(file => file.id)
              .map(file => ({
                id: file.id,
                fileName: file.displayName || file.name,
              })),
          ),
        )
      }

      return formData
    }

    const handleSubmit = async () => {
      if (submitting.value) return

      submitting.value = true
      try {
        const formData = buildFormData()
        if (isEdit.value) {
          await notulensiAPI.update(route.params.id, formData)
        } else {
          await notulensiAPI.create(formData)
        }

        notification.success({
          message: isEdit.value ? 'Notulensi Diubah' : 'Notulensi Dibuat',
          description: isEdit.value
            ? 'Data notulensi berhasil diubah.'
            : 'Data notulensi berhasil disimpan.',
        })
        confirmModal.value = false
        router.push('/notulensi')
      } catch (error) {
        notification.error({
          message: 'Gagal Menyimpan',
          description: error.response?.data?.message || 'Data notulensi gagal disimpan.',
        })
      } finally {
        submitting.value = false
      }
    }

    const goBack = () => {
      router.push('/notulensi')
    }

    onMounted(() => {
      if (!isSekretaris.value) {
        notification.warning({
          message: 'Akses Ditolak',
          description: 'Halaman tambah dan edit notulensi hanya untuk sekretaris.',
        })
        router.replace('/notulensi')
        return
      }

      if (isEdit.value) {
        fetchDetail()
      }
    })

    return {
      addLink,
      confirmModal,
      deletedFileIds,
      editedFileName,
      editingFileUid,
      fileList,
      formRef,
      formState,
      goBack,
      handleFileChange,
      handleSubmit,
      isEdit,
      loading,
      openConfirmModal,
      openFile,
      removeFile,
      removeLink,
      rulesForm,
      saveFileName,
      startEditFileName,
      submitting,
    }
  },
})
</script>

<style lang="scss" scoped>
.notulensi-form-page {
  background: #fcfcfc;
  min-height: 100vh;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.page-header {
  margin-bottom: 20px;
}

.th-title {
  color: #262626;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

.form-card {
  border-radius: 8px;

  h2 {
    color: #262626;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 20px;
  }
}

.w-100 {
  width: 100%;
}

.upload-icon {
  color: #e10f28;
}

.file-list {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
}

.custom-icon-unggah {
  color: #e10f28 !important;
  cursor: pointer;
}

.custom-text-anchor-not-clickable {
  color: #321e8d;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #e10f28;
  }
}

.file-link {
  align-items: center;
  display: inline-flex;
  gap: 4px;
  text-decoration: none;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    white-space: normal;
    word-break: break-word;
  }

  &:hover {
    text-decoration: underline;
  }
}

.file-paperclip-icon {
  color: rgba(0, 0, 0, 0.45);
}

.file-name-input {
  max-width: 320px;
}

.file-action-icon {
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s ease-in-out;

  &:hover,
  &:focus {
    color: #e10f28;
  }
}

.icon-button {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  color: #595959;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  transition: all 0.2s ease;
  width: 32px;

  &:hover,
  &:focus {
    border-color: #e10f28;
    color: #e10f28;
  }

  &.danger:hover,
  &.danger:focus {
    border-color: #e10f28;
    color: #e10f28;
  }
}

.link-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

.link-row {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    .icon-button {
      justify-self: flex-end;
    }
  }
}

.btn-add-link {
  border-radius: 24px;
  font-weight: 600;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;

  @media (max-width: 576px) {
    flex-direction: column-reverse;
  }
}

.button-primary,
.button-secondary {
  border-radius: 24px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  min-height: 40px;
  min-width: 120px;
  padding: 8px 18px;
  transition: all 0.2s ease-in-out;

  @media (max-width: 576px) {
    width: 100%;
  }
}

.button-primary {
  background: #e10f28;
  border: 2px solid #e10f28;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #ffffff;
    color: #e10f28;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.button-secondary {
  background: #ffffff;
  border: 2px solid #262626;
  color: #262626;

  &:hover {
    background: #262626;
    color: #ffffff;
  }
}

.confirm-copy {
  color: #595959;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;

  @media (max-width: 576px) {
    flex-direction: column;
  }
}

.modal-button {
  border-radius: 24px;
  cursor: pointer;
  flex: 1;
  font-weight: 700;
  min-height: 40px;
  padding: 8px 14px;
  transition: all 0.2s ease;

  &.secondary {
    background: #ffffff;
    border: 2px solid #262626;
    color: #262626;

    &:hover {
      background: #262626;
      color: #ffffff;
    }
  }

  &.primary {
    background: #e10f28;
    border: 2px solid #e10f28;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #ffffff;
      color: #e10f28;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
